/**
 * 회사 정보 헬퍼 (단일 소스: config/company.php)
 *
 * Blade 레이아웃이 `window.JadEye.company` 로 주입한 글로벌 객체를
 * 타입 안전하게 접근하기 위한 헬퍼입니다.
 *
 * 변경 시 반드시 config/company.php 를 먼저 수정하세요.
 * 관련 문서: docs/business/legal-docs-management.md
 */

export interface CompanyProcessor {
    name: string;
    category: string;
    purpose: string;
    items: string[];
    period: string;
    country: string;
}

export interface CompanyOverseasTransfer {
    name: string;
    country: string;
    services: string[];
    purpose: string;
    items: string[];
    period: string;
    method: string;
    contact: string;
}

export interface CompanyDisputeResolution {
    name: string;
    url: string;
    tel: string;
}

export interface CompanyVentureCertification {
    is_certified: boolean;
    type: string;
    certificate_no: string;
    issued_at: string;
    valid_from: string;
    valid_until: string;
    issuer: string;
    mark_horizontal: string;
    mark_vertical: string;
    mark_emblem: string;
}

export interface CompanyInfo {
    legal_name: string;
    brand_name: string;
    service_name: string;
    representative: string;
    business_number: string;
    mail_order_number: string | null;
    publisher_number?: string | null; // 출판사 신고번호 — Ink 대문 푸터에만 밝힌다
    publisher_office?: string | null;
    address: string;
    address_detail: string;
    postal_code: string;
    phone: string;
    fax: string;
    email: {
        general: string;
        privacy: string;
        business: string;
        legal: string;
        security: string;
    };
    privacy_officer: {
        name: string;
        position: string;
        department: string;
        email: string;
        phone: string;
    };
    privacy_request_desk: {
        department: string;
        email: string;
        phone: string;
    };
    website: string;
    app_website: string;
    admin_website: string;
    founded_year: number;
    copyright_holder: string;
    policies: {
        privacy_version: string;
        privacy_effective: string;
        privacy_last_revised: string;
        terms_version: string;
        terms_effective: string;
        terms_last_revised: string;
    };
    retention: Record<string, string>;
    processors: CompanyProcessor[];
    overseas_transfers: CompanyOverseasTransfer[];
    dispute_resolution: CompanyDisputeResolution[];
    venture_certification?: CompanyVentureCertification;
}

declare global {
    interface Window {
        JadEye?: {
            user?: unknown;
            appName?: string;
            csrf?: string;
            company?: CompanyInfo;
        };
    }
}

/**
 * 회사 정보 반환. Blade 주입 실패 시 빈 fallback 반환 (앱 크래시 방지).
 */
export function getCompany(): CompanyInfo {
    if (typeof window !== 'undefined' && window.JadEye?.company) {
        return window.JadEye.company;
    }
    // Blade 에서 주입되지 않은 경우 (SSR/테스트 환경 등)
    throw new Error('window.JadEye.company 가 주입되지 않았습니다. Blade 레이아웃을 확인하세요.');
}

/**
 * Copyright 문자열 포맷터 — "© 2026~ JadEye Inc. All rights reserved."
 */
export function formatCopyright(company: CompanyInfo = getCompany()): string {
    return `© ${company.founded_year}~ ${company.copyright_holder} All rights reserved.`;
}

/**
 * 푸터용 한 줄 회사 정보 — "주식회사 제이드아이 | 개인정보보호책임자: 김제이 | 사업자등록번호: 652-86-03966"
 */
export function formatFooterLine(company: CompanyInfo = getCompany()): string {
    return [company.legal_name, `개인정보보호책임자: ${company.privacy_officer.name}`, `사업자등록번호: ${company.business_number}`].join(' | ');
}
